import { useEffect, useMemo, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search } from "lucide-react";
import { staggerContainer, staggerItem } from "@/components/tex/PageTransition";
import { api } from "@/api";

export default function ProductsPage() {
  const { company, openQuote } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    document.title = `Products — ${company?.name || "Texcheme"}`;
  }, [company]);

  useEffect(() => {
    let alive = true;
    api.getSiteContent()
      .then((site) => { if (alive) setProducts(site.products || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => { if (p.category) set.add(p.category); });
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (category !== "all" && p.category !== category) return false;
      if (!q) return true;
      const hay = [p.name, p.formula, p.category, ...(p.grades || [])]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [products, query, category]);

  return (
    <>
      <header className="page-hero" data-testid="products-hero">
        <div className="wrap">
          <div className="section-eyebrow">Product catalogue</div>
          <h1 data-testid="products-title">All bulk chemicals we supply.</h1>
          <p className="page-hero-lead">
            {products.length} industrial chemicals — acids, alkalis, sulphates, nitrates,
            hypochlorites and specialty salts. Full tanker loads, tonne-level pricing and
            batch-wise COAs on every dispatch.
          </p>
        </div>
      </header>

      <section className="section-lg products-band" data-testid="products-full-band">
        <motion.div
          className="wrap"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Search + category filter */}
          <div className="products-controls" data-testid="products-controls">
            <div className="products-search">
              <Search size={16} strokeWidth={2.2} />
              <input
                type="text"
                placeholder="Search by name, formula or grade…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="products-search-input"
                aria-label="Search products"
              />
            </div>
            <div className="products-filters" role="tablist" aria-label="Category filter">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={"filter-chip" + (category === c ? " on" : "")}
                  onClick={() => setCategory(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c === "all" ? `All (${products.length})` : `${c} (${products.filter((p) => p.category === c).length})`}
                </button>
              ))}
            </div>
          </div>

          {/* Result count */}
          <div style={{ marginBottom: 16, fontSize: 13, color: "var(--tc-slate)" }} data-testid="products-result-count">
            Showing <b style={{ color: "var(--tc-ink)" }}>{filtered.length}</b> of {products.length} chemicals
            {query && <> matching &ldquo;{query}&rdquo;</>}
            {category !== "all" && <> in {category}</>}.
          </div>

          {filtered.length === 0 ? (
            <div className="products-empty" data-testid="products-empty">
              No chemicals match your search. Try a different keyword or clear the filter.
            </div>
          ) : (
            <motion.div className="products-grid" data-testid="products-full-grid" variants={staggerItem}>
              {filtered.map((p, i) => {
                const testId = `product-full-${(p.key || p.name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${i}`;
                return (
                  <div
                    key={testId}
                    className={"product-card" + (p.has_tools ? " has-tools" : "")}
                    data-testid={testId}
                  >
                    <div
                      className="product-formula"
                      style={{ background: p.color + "18", color: p.color }}
                    >
                      {p.formula}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="product-name">{p.name}</div>
                      <div className="product-grades">{p.grades?.join(" · ")}</div>
                      {p.category && <div className="product-category">{p.category}</div>}
                    </div>
                    {p.has_tools && (
                      <Link
                        to="/tools"
                        className="product-tools-badge"
                        data-testid={`${testId}-tools`}
                        title="Safety & compliance data available"
                      >
                        Tools →
                      </Link>
                    )}
                  </div>
                );
              })}
            </motion.div>
          )}

          <div style={{ marginTop: 36, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              className="btn btn-brand"
              onClick={() => openQuote("products-page")}
              data-testid="products-request-quote"
            >
              Request a quote <ArrowRight size={16} strokeWidth={2.5} />
            </button>
            <Link to="/tools" className="btn btn-ghost-dark" data-testid="products-open-tools">
              Safety &amp; compliance tools
            </Link>
          </div>
        </motion.div>
      </section>
    </>
  );
}
